import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import RadioCardGroup from "../../../../components/form-item/RadioCardGroup";
import Button from "../../../../components/button/Button";
import DropdownMenu from "../../../../components/dropdown-menu/DropdownMenu";
import Modal from "../../../../components/modal/Modal";
import DateInput from "../../../../components/calendar/DateInput";
import { CircularFab } from "../../../../components/fab";
import { ProjectPrimaryActionSlot } from "../../../../components/bottom-app-bar/ProjectPrimaryAction";
import { useObservedHeight } from "../../../../hooks/useObservedHeight";
import useElementRect from "../../../../hooks/useElementRect";
import useShotApproval from "../../../../hooks/useShotApproval";
import useAnimatedStatusChange from "../../../../hooks/useAnimatedStatusChange";
import {
  Shot,
  useUpdateShotStatusAnimatedMutation,
  useUpdateShotMutation,
} from "../../../../app/services/shots";
import {
  useReactToNoteMutation,
  type TodoNoteCard,
} from "../../../../app/services/notes";
import { isClientGroupPosition } from "../../../../utils/memberPositionUtils";
import {
  startSharedMediaTransition,
  WHEEL_SHOT_MEDIA_ATTR,
} from "../../../../utils/sharedMediaTransition";
import { ShotStatus } from "../../../../types/home";
import type { PreviewQueue } from "../../../../types/previewQueue";

import ShotPortraitCard from "./ShotPortraitCard";
import TipPortraitCard from "./TipPortraitCard";
import WaitingLabelCard from "./WaitingLabelCard";
import WaitingShotCloud from "./WaitingShotCloud";
import ShotCommentCloud from "./ShotCommentCloud";

/**
 * HOME「進行中」タブのホイール表示。
 *
 * プロジェクトリスト進行中タブ（ProjectWheel）と同じ構成:
 * RadioCardGroup（GSAP、円 → 扇 → ホイール回転をスクロール操作だけで完結）に
 * やることリスト（必須TIPS → ショット → 通常TIPS の順を維持）を縦型大判カードで
 * 並べ、末尾に「待機中スロット」を常駐させる（センチネルパターン）。
 * 待機中スロットはカード枠を持たず中央にラベル（鋭意制作中 / レビュー待ち）のみを
 * 表示し、選択中はラベルの周囲に待機中ショットのサムネイルチップを雲状に散らす
 * （WaitingShotCloud。チップクリックで Preview へ共有エレメント遷移）。
 *
 * 下部には viewport 固定のアクションパネル（旧 TodoShotCard / TodoTipCard の
 * ボタン群を移植。document.body へ Portal）を重ねる。
 * 待機中スロット選択時はパネル自体を表示しない。
 *
 * 相手側の未解決メッセージはパネルではなく、中央カードの周辺に雲状に散らして
 * 表示する（ShotCommentCloud）。カードが中央にナッジしきった（settled）
 * タイミングで 1 件ずつフェードインする。
 *
 * 自動再生はホイール中央（選択中）のショットカードのみ（ミュートループ）。
 * ホバー再生は廃止。カードのシングルクリック / タップは選択のみで、Preview への
 * 遷移は Enter キー・ダブルクリック（いずれも RadioCardGroup の onConfirm）か
 * アクションパネルの返信系ボタンに限定する（誤タップでの遷移防止）。遷移時は
 * 共有エレメント遷移（startSharedMediaTransition）でカードのメディアが Preview
 * のメディア表示位置へシームレスに収まる。
 */

// ===== 定数 =====

/** ホイール末尾の「待機中まとめ」センチネルカードの ID。 */
const WAITING_CARD_ID = "__waiting__";
/**
 * 待機ショットチップ（body ポータル）をこのホイールのジェスチャ面として
 * 結びつけるキー（RadioCardGroup の gestureSurfaceKey / WaitingShotCloud の
 * wheelGestureKey で共有）。
 */
const WHEEL_GESTURE_KEY = "home-in-progress-wheel";

/**
 * 上部クローム高さ: GlobalHeader(64) + ProjectBanner(75) + TopAppBarHome(48)。
 * スクロール操作で scrollState が "down"（GlobalHeader / BottomAppBar 非表示）に
 * なることがあるが、カードサイズはガタつき防止のため常に "up"（全クローム表示）
 * 基準で固定し、下部パネルの bottom だけ scrollState に追従させる。
 */
const TOP_CHROME_PX = 64 + 75 + 48;
/** BottomAppBarProject の高さ（--bottom-app-bar-height と同値）。 */
const BOTTOM_APP_BAR_PX = 80;
/** カード領域の上下に確保する呼吸余白(px)。 */
const CARD_REGION_PADDING_PX = 16;
/** パネルと下端（BottomAppBar 上端 / viewport 下端）の距離(px)。 */
const PANEL_MARGIN_PX = 16;
/**
 * カードサイズ計算用の下部確保量（アクションパネルの bottom 相当）。
 * BottomAppBar が隠れてもカードサイズは変えない（表示時基準で固定）ため、
 * 常にバー表示時の値で確保する。実際のパネル位置は panelBottomBase が
 * scrollState に追従して決める。
 */
const RESERVED_PANEL_BOTTOM_PX = BOTTOM_APP_BAR_PX + PANEL_MARGIN_PX;
/** カード拡大・メッセージ雲の下限とアクションパネル上端の間隔(px)。 */
const PANEL_GAP_PX = 8;
/** カード高さの下限(px)。 */
const MIN_CARD_HEIGHT_PX = 320;
/** カード左右に最低限確保する余白(px)。 */
const MIN_HORIZONTAL_INSET_PX = 48;
/** レイアウト再計算のジッター抑止しきい値(px)。 */
const LAYOUT_DAMPING_PX = 8;
/** 白銀比（カード縦横比 1:√2）。 */
const SILVER_RATIO = Math.SQRT2;
/** メッセージ雲を配置してよい領域の viewport 端からの余白(px)。 */
const CLOUD_EDGE_INSET_PX = 8;
/** メッセージ雲の表示完了からアクションパネルをふわっと出すまでの間(ms)。 */
const PANEL_AFTER_CLOUD_MS = 250;
/** 雲を持たないカード（TIPS 等）で settled からパネルを出すまでの間(ms)。 */
const PANEL_REVEAL_DELAY_MS = 450;
/** 雲のコメント取得が確定しない場合にパネル表示を強行する保険(ms)。 */
const PANEL_REVEAL_FALLBACK_MS = 2000;

// ===== 型 =====

/** やることリストの混在アイテム（ショット / 共有TIPSカード）。 */
export type TodoListItem =
  | { kind: "shot"; shot: Shot }
  | { kind: "tip"; card: TodoNoteCard };

type WheelItem =
  | { id: string; kind: "shot"; shot: Shot }
  | { id: string; kind: "tip"; card: TodoNoteCard }
  | { id: string; kind: "waiting" };

export interface InProgressWheelProps {
  todoItems: TodoListItem[];
  waitingItems: Shot[];
  projectUserPosition: string;
  /**
   * タブが表示中か（Preview 表示中は false）。下部パネルは document.body へ
   * Portal しており Activity の display:none が効かないため、ここが false の
   * 間は Portal ごと描画しない。
   */
  active: boolean;
  onShotStatusChanged?: () => void;
}

// ===== Styled =====

/**
 * ホイールのスクローラ。`data-offer-overlay-scroll` を RadioCardGroup が
 * 自動検出して scroll listener を貼る（ProjectWheel と同じ仕組み）。
 */
const WheelScroller = styled.div`
  height: calc(100vh - ${TOP_CHROME_PX + BOTTOM_APP_BAR_PX}px);
  height: calc(100dvh - ${TOP_CHROME_PX + BOTTOM_APP_BAR_PX}px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * アクションパネル（ProjectWheel と同じグラスモーフィズム）。
 * スクロール移動中は `$visible=false` で隠し、カードが settle して
 * メッセージ雲の表示が終わったあとに少し下からふわっとフェードインする
 * （ProjectWheel の scrollFadeCss と同じ質感）。opacity/transform のみの
 * 切替なので useObservedHeight の高さ計測（actionReserve）には影響しない。
 */
const ActionPanel = styled.div<{
  $bottom: number;
  $left: number;
  $width: number;
  $visible: boolean;
}>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? 0 : 6)}px);
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  /* bottom は BottomAppBar（StyledBottomAppBar）のスライドと同じタイミングで追従 */
  transition: ${({ $visible }) =>
    $visible
      ? "bottom 0.2s, opacity 0.45s ease, transform 0.45s ease"
      : "bottom 0.2s, opacity 0.15s ease, transform 0.15s ease"};
  z-index: 3;
  padding: 8px 12px;

  border-radius: 6px;
  border: 1px solid #f0f0f0;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

/** ステータス変更時のフェードアウト用ラッパー（カード全体に適用）。 */
const CardFadeWrapper = styled.div<{ $fading: boolean }>`
  width: 100%;
  height: 100%;
  ${({ $fading }) => ($fading ? "pointer-events: none;" : "")}
`;

// ===== アクションパネル: ショット =====

interface ShotActionsProps {
  shot: Shot;
  projectUserPosition: string;
  // eslint-disable-next-line no-unused-vars
  onNavigateToPreview: (shot: Shot, openShotEditModal?: boolean) => void;
  onShotStatusChanged?: () => void;
  // eslint-disable-next-line no-unused-vars
  onFadingChange: (shotId: string, fading: boolean) => void;
}

const ShotActions: React.FC<ShotActionsProps> = ({
  shot,
  projectUserPosition,
  onNavigateToPreview,
  onShotStatusChanged,
  onFadingChange,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const shotId = shot.id;

  const [updateShotStatus] = useUpdateShotStatusAnimatedMutation();
  const [updateShot] = useUpdateShotMutation();

  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(() => {
    if (shot.next_delivery_date) return new Date(shot.next_delivery_date);
    const d = new Date();
    d.setHours(10, 0, 0, 0);
    return d;
  });

  const { fadingOut, executeWithAnimation } = useAnimatedStatusChange({
    onComplete: onShotStatusChanged,
  });

  // カード側のフェードアウト表示と同期させる
  useEffect(() => {
    onFadingChange(shotId, fadingOut);
    return () => onFadingChange(shotId, false);
  }, [fadingOut, shotId, onFadingChange]);

  // 検収ON/OFF（旧 TodoShotCard と同じ共通 hook）
  const {
    isApproved,
    canApprove,
    isLoading: isApprovalLoading,
    toggleApproval,
  } = useShotApproval({
    projectId: projectId || "",
    shotId: shotId || "",
    currentStatus: shot.status,
    onStatusChanged: onShotStatusChanged,
    skipOnStatusChanged: true,
    useAnimatedMutation: true,
  });

  const handleToggleApproval = useCallback(() => {
    if (!isApproved) {
      window.dispatchEvent(new CustomEvent("approval-start"));
    }
    const delay = isApproved ? 0 : 1500;
    executeWithAnimation(() => toggleApproval(), { delay });
  }, [isApproved, toggleApproval, executeWithAnimation]);

  const handleSaveDeliveryDate = useCallback(async () => {
    if (!projectId || !shotId || !deliveryDate) return;
    try {
      const y = deliveryDate.getFullYear();
      const m = String(deliveryDate.getMonth() + 1).padStart(2, "0");
      const d = String(deliveryDate.getDate()).padStart(2, "0");
      const h = String(deliveryDate.getHours()).padStart(2, "0");
      const min = String(deliveryDate.getMinutes()).padStart(2, "0");
      await updateShot({
        project_id: projectId,
        shot_id: shotId,
        next_delivery_date: `${y}-${m}-${d}T${h}:${min}:00Z`,
      }).unwrap();
      setShowDeliveryDateModal(false);
      onShotStatusChanged?.();
    } catch (error) {
      console.error("提出予定日の更新エラー:", error);
    }
  }, [projectId, shotId, deliveryDate, updateShot, onShotStatusChanged]);

  const handleClientPending = useCallback(() => {
    if (!projectId || !shotId) return;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: ShotStatus.CLIENT_PENDING,
      }).unwrap(),
    );
  }, [projectId, shotId, updateShotStatus, executeWithAnimation]);

  const handleSetHold = useCallback(() => {
    if (!projectId || !shotId) return;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED,
      }).unwrap(),
    );
  }, [projectId, shotId, updateShotStatus, executeWithAnimation]);

  const handleCancelHold = useCallback(() => {
    if (!projectId || !shotId) return;
    const revertStatus = isClientGroupPosition(projectUserPosition)
      ? ShotStatus.CLIENT_PENDING
      : ShotStatus.PRODUCER_PENDING;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: revertStatus,
      }).unwrap(),
    );
  }, [projectId, shotId, updateShotStatus, projectUserPosition, executeWithAnimation]);

  if (isClientGroupPosition(projectUserPosition)) {
    return (
      <>
        <Button
          size="small"
          theme="tertiary"
          variant="filled"
          leftIcon="reply"
          width="fit"
          onClick={() => onNavigateToPreview(shot)}
        >
          詳しくチェック
        </Button>
        <CircularFab
          isActive={isApproved}
          onClick={handleToggleApproval}
          disabled={!canApprove || isApprovalLoading || fadingOut}
          variant="client"
          size={48}
        />
      </>
    );
  }

  return (
    <>
      <Button
        size="small"
        theme="tertiary"
        variant="filled"
        leftIcon="reply"
        width="fit"
        onClick={() => onNavigateToPreview(shot)}
      >
        返信
      </Button>
      <div className="u-row u-gap8 u-items-center">
        <DropdownMenu
          placement="top-end"
          items={[
            {
              label: "提出予定日を設定",
              icon: "event",
              onClick: () => setShowDeliveryDateModal(true),
            },
            {
              label: "クライアントタスクにする",
              icon: "shortcut",
              onClick: handleClientPending,
            },
            ...(shot.status === ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED
              ? [
                  {
                    label: "保留を取り消す",
                    icon: "undo",
                    onClick: handleCancelHold,
                  },
                ]
              : [
                  {
                    label: "このショットを保留",
                    icon: "do_not_disturb_on",
                    onClick: handleSetHold,
                  },
                ]),
          ]}
        />
        <Button
          rightIcon="upload"
          size="small"
          theme="primary"
          variant="filled"
          onClick={() => onNavigateToPreview(shot, true)}
        >
          提出
        </Button>
      </div>
      {showDeliveryDateModal && (
        <Modal
          onClose={() => setShowDeliveryDateModal(false)}
          title="提出予定日を設定"
          primaryAction={{
            label: "設定",
            onClick: handleSaveDeliveryDate,
            disabled: !deliveryDate,
          }}
          secondaryAction={{
            label: "キャンセル",
            onClick: () => setShowDeliveryDateModal(false),
          }}
        >
          <DateInput
            label="提出予定日"
            placeholder="日付を選択"
            value={deliveryDate}
            minDate={new Date()}
            dateFormat="Y年n月j日（D）H:i"
            options={{
              static: false,
              enableTime: true,
              time_24hr: true,
              defaultHour: 10,
              defaultMinute: 0,
            }}
            onChange={(e: { target: { value: Date } }) =>
              setDeliveryDate(e.target.value)
            }
          />
        </Modal>
      )}
    </>
  );
};

// ===== アクションパネル: TIPS =====

const TipActions: React.FC<{ card: TodoNoteCard; projectId: string }> = ({
  card,
  projectId,
}) => {
  const [reactToCard, { isLoading: reacting }] = useReactToNoteMutation();

  const handleOk = useCallback(() => {
    // 楽観更新（notes.ts 側）でリストから即時除去される
    void reactToCard({ note_id: card.id, ok: true, project_id: projectId });
  }, [reactToCard, card.id, projectId]);

  // 「役に立った」も OK と同じく既読扱いにしてカードを閉じる（helpful + ok）。
  const handleHelpful = useCallback(() => {
    void reactToCard({
      note_id: card.id,
      helpful: true,
      ok: true,
      project_id: projectId,
    });
  }, [reactToCard, card.id, projectId]);

  return (
    <>
      <Button
        size="small"
        theme="secondary"
        variant="text"
        leftIcon="thumb_up"
        width="fit"
        onClick={handleHelpful}
        disabled={reacting}
      >
        役に立った
      </Button>
      <Button
        size="small"
        theme="secondary"
        variant="filled"
        rightIcon="check"
        onClick={handleOk}
        disabled={reacting}
      >
        OK
      </Button>
    </>
  );
};

// ===== 本体 =====

const InProgressWheel: React.FC<InProgressWheelProps> = ({
  todoItems,
  waitingItems,
  projectUserPosition,
  active,
  onShotStatusChanged,
}) => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  // BottomAppBar のスクロール連動（"down" でバーが隠れる）に合わせて
  // パネルの bottom も追従させる。
  const scrollState = useSelector(
    (state: { layout: string }) => state.layout,
  );
  const bottomBarVisible = scrollState !== "down";

  const isClientGroup = isClientGroupPosition(projectUserPosition);
  const waitingTitle = isClientGroup ? "鋭意制作中" : "レビュー待ち";

  const items = useMemo<WheelItem[]>(() => {
    const mapped = todoItems.map((item): WheelItem =>
      item.kind === "shot"
        ? { id: item.shot.id, kind: "shot", shot: item.shot }
        : { id: `tip-${item.card.id}`, kind: "tip", card: item.card },
    );
    if (waitingItems.length > 0) {
      mapped.push({ id: WAITING_CARD_ID, kind: "waiting" });
    }
    return mapped;
  }, [todoItems, waitingItems.length]);

  // ── ショット連番（旧 TodoShotCard の Counter。TIPS を除いて数える） ──
  const shotNumbers = useMemo(() => {
    const map = new Map<string, number>();
    let n = 0;
    for (const item of items) {
      if (item.kind === "shot") {
        n += 1;
        map.set(item.id, n);
      }
    }
    return map;
  }, [items]);

  // ── 選択状態（ホイール中央のカード = 選択中） ──
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const effectiveSelectedId =
    selectedId !== undefined && items.some((i) => i.id === selectedId)
      ? selectedId
      : items[0]?.id;
  const selectedItem = items.find((i) => i.id === effectiveSelectedId);

  // ── ステータス変更中（フェードアウト中）のショット ──
  const [fadingShotId, setFadingShotId] = useState<string | null>(null);
  const handleFadingChange = useCallback((shotId: string, fading: boolean) => {
    setFadingShotId((prev) => {
      if (fading) return shotId;
      return prev === shotId ? null : prev;
    });
  }, []);

  // ── Preview への遷移（共有エレメント遷移） ──

  // ⌥[ / ⌥] でやることカードのショット間を移動するための並び
  // （TIPS・待機カードを除いた表示順。Preview 側は usePreviewQueueShortcut が読む）
  const todoShotIds = useMemo(
    () =>
      todoItems
        .filter(
          (item): item is Extract<TodoListItem, { kind: "shot" }> =>
            item.kind === "shot",
        )
        .map((item) => item.shot.id),
    [todoItems],
  );

  const navigateToPreview = useCallback(
    (shot: Shot, openShotEditModal = false, sourceEl?: HTMLElement | null) => {
      if (!projectId || !shot.id) return;
      const el =
        sourceEl ??
        document.querySelector<HTMLElement>(
          `[${WHEEL_SHOT_MEDIA_ATTR}="${shot.id}"]`,
        );
      if (el) {
        startSharedMediaTransition({
          sourceEl: el,
          imageSrc: shot.thumbnail ?? undefined,
        });
      }
      const previewQueue: PreviewQueue = {
        source: "todo",
        shotIds: todoShotIds,
      };
      navigate(`/${projectId}/${shot.id}`, {
        state: {
          shot,
          ...(openShotEditModal ? { openShotEditModal: true } : {}),
          sharedTransition: !!el,
          ...(todoShotIds.length > 0 ? { previewQueue } : {}),
        },
      });
    },
    [navigate, projectId, todoShotIds],
  );

  // クリック / スクロールは選択のみ（誤タップでの遷移を防ぐ）。
  const handleWheelChange = useCallback((id: string | number) => {
    setSelectedId(String(id));
  }, []);

  // Preview への遷移は Enter キー（決定）とアクションパネルのボタン経由のみ。
  const handleWheelConfirm = useCallback(
    (_id: string | number, item: WheelItem) => {
      if (item.kind === "shot") {
        navigateToPreview(item.shot);
      }
    },
    [navigateToPreview],
  );

  // ── viewport 追従 ──
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── 下部パネルの実高計測（ProjectWheel と同じ単調増加 reserve 方式） ──
  const { ref: actionRef, height: actionHeightRaw } =
    useObservedHeight<HTMLDivElement>();

  const [actionReserve, setActionReserve] = useState(0);
  useEffect(() => {
    setActionReserve((prev) => Math.max(prev, actionHeightRaw));
  }, [actionHeightRaw]);

  // ── カードサイズ算出（アクションパネル分を確保。メッセージ雲はカードに
  //    被せるオーバーレイなのでカードサイズには影響させない） ──
  const rawLayout = useMemo(() => {
    const topOffset = TOP_CHROME_PX + CARD_REGION_PADDING_PX;
    const reservedBottom =
      RESERVED_PANEL_BOTTOM_PX + actionReserve + CARD_REGION_PADDING_PX;
    const freeHeight = viewport.height - topOffset - reservedBottom;

    let cardHeight = Math.max(MIN_CARD_HEIGHT_PX, freeHeight);
    let cardWidth = cardHeight / SILVER_RATIO;
    const maxCardWidth = Math.max(
      240,
      viewport.width - MIN_HORIZONTAL_INSET_PX * 2,
    );
    if (cardWidth > maxCardWidth) {
      cardWidth = maxCardWidth;
      cardHeight = cardWidth * SILVER_RATIO;
    }

    const centerY =
      freeHeight > 0 ? topOffset + freeHeight / 2 : viewport.height / 2;

    return {
      cardWidth: Math.round(cardWidth),
      cardHeight: Math.round(cardHeight),
      centerY: Math.round(centerY),
    };
  }, [viewport, actionReserve]);

  const [layout, setLayout] = useState(rawLayout);
  useEffect(() => {
    setLayout((prev) => {
      const stable =
        Math.abs(prev.cardWidth - rawLayout.cardWidth) < LAYOUT_DAMPING_PX &&
        Math.abs(prev.cardHeight - rawLayout.cardHeight) < LAYOUT_DAMPING_PX &&
        Math.abs(prev.centerY - rawLayout.centerY) < LAYOUT_DAMPING_PX;
      return stable ? prev : rawLayout;
    });
  }, [rawLayout]);

  const cardSize = useMemo(
    () => ({ width: layout.cardWidth, height: layout.cardHeight }),
    [layout.cardWidth, layout.cardHeight],
  );

  /**
   * アクションパネル領域の上端（viewport Y）。パネルは表示時基準
   * （RESERVED_PANEL_BOTTOM_PX）で固定なので、TIPS カード拡大の下限と
   * メッセージ雲の配置下限の共通基準になる。
   */
  const panelTopLimit =
    viewport.height - (RESERVED_PANEL_BOTTOM_PX + actionReserve + PANEL_GAP_PX);

  // ── TIPS カード選択中の拡大率 ──
  // TIPS カードはメッセージ雲を持たないため、本文を読みやすくするよう
  // 上端固定（transform-origin: top）で下方向に伸ばし、アクションパネルの
  // 直上まで・左右はビューポート内に収まる範囲で最大化する。
  const tipEnlargeScale = useMemo(() => {
    if (layout.cardHeight <= 0 || layout.cardWidth <= 0) return 1;
    const cardTop = layout.centerY - layout.cardHeight / 2;
    const heightScale = (panelTopLimit - cardTop) / layout.cardHeight;
    const widthScale = (viewport.width - 32) / layout.cardWidth;
    return Math.max(1, Math.min(heightScale, widthScale));
  }, [layout, viewport.width, panelTopLimit]);

  // ── 下部パネルの水平位置 ──
  // 幅はやることカード（ホイールのスロット）に合わせる。
  const anchorRef = useRef<HTMLDivElement>(null);
  const anchorRect = useElementRect(anchorRef);
  const anchorWidth = anchorRect.width || viewport.width;
  const panelWidth = Math.min(anchorWidth, cardSize.width);
  const panelLeft = anchorRect.left + (anchorWidth - panelWidth) / 2;

  // BottomAppBar 非表示時はバーの高さぶん下げる（カードサイズは変えない）
  const actionPanelBottom =
    (bottomBarVisible ? BOTTOM_APP_BAR_PX : 0) + PANEL_MARGIN_PX;

  // ── ホイールのナッジ完了（settled）検知 ──
  // メッセージ雲のフェードイン起点と TIPS カードの拡大タイミングに使う。
  const [wheelSettled, setWheelSettled] = useState(false);
  const handleSettleChange = useCallback((settled: boolean) => {
    setWheelSettled(settled);
  }, []);

  // ── スタックプレビュー（Shift ホールド） ──
  // スタック中はメッセージ雲・アクションパネル・TIPS 拡大を止めて、
  // カードの山と件数ラベルだけの画面にする。
  const [stacked, setStacked] = useState(false);
  const handleStackChange = useCallback((stackActive: boolean) => {
    setStacked(stackActive);
  }, []);

  // ── アクションパネルのふんわり表示 ──
  // ProjectWheel と同じく移動中は隠す。ショットはメッセージ雲の表示完了
  // （onRevealComplete）からさらに少し置いて、TIPS などはカードが settle して
  // 表示が伸びきってから少し置いてフェードインする。
  const [panelVisible, setPanelVisible] = useState(false);
  const panelRevealTimerRef = useRef<number | null>(null);
  const clearPanelRevealTimer = useCallback(() => {
    if (panelRevealTimerRef.current !== null) {
      window.clearTimeout(panelRevealTimerRef.current);
      panelRevealTimerRef.current = null;
    }
  }, []);
  const schedulePanelReveal = useCallback(
    (delayMs: number) => {
      clearPanelRevealTimer();
      panelRevealTimerRef.current = window.setTimeout(() => {
        panelRevealTimerRef.current = null;
        setPanelVisible(true);
      }, delayMs);
    },
    [clearPanelRevealTimer],
  );

  // 雲の表示完了通知を受け取ったか。子（Cloud）の effect は親の effect より
  // 先に実行されるため、通知済みのときに保険タイマーで予約を上書きしない。
  const cloudRevealedRef = useRef(false);

  const selectedItemId = selectedItem?.id;
  const selectedItemKind = selectedItem?.kind;
  useEffect(() => {
    if (!wheelSettled || !selectedItemId) {
      clearPanelRevealTimer();
      setPanelVisible(false);
      cloudRevealedRef.current = false;
      return undefined;
    }
    if (selectedItemKind === "shot") {
      // 原則は ShotCommentCloud の onRevealComplete が表示を引き取る。
      // ここはコメント取得が確定しないケースの保険としてだけ働く。
      if (!cloudRevealedRef.current) {
        schedulePanelReveal(PANEL_REVEAL_FALLBACK_MS);
      }
    } else {
      schedulePanelReveal(PANEL_REVEAL_DELAY_MS);
    }
    return clearPanelRevealTimer;
  }, [
    wheelSettled,
    selectedItemId,
    selectedItemKind,
    schedulePanelReveal,
    clearPanelRevealTimer,
  ]);

  const handleCloudRevealComplete = useCallback(() => {
    cloudRevealedRef.current = true;
    schedulePanelReveal(PANEL_AFTER_CLOUD_MS);
  }, [schedulePanelReveal]);

  // ── メッセージ雲の配置基準 ──
  // 中央カードの viewport 矩形。breakOut ステージはカードを `left: 50%` に
  // 置く（HOME に .dm-two-column-wrapper は無いのでステージ中央 ≒ viewport 中央）。
  const cloudCardRect = useMemo(
    () => ({
      left: viewport.width / 2 - layout.cardWidth / 2,
      top: layout.centerY - layout.cardHeight / 2,
      width: layout.cardWidth,
      height: layout.cardHeight,
    }),
    [viewport.width, layout],
  );
  // バブルを置いてよい領域: 上部クロームの下 〜 アクションパネルの上。
  const cloudBounds = useMemo(() => {
    const top = TOP_CHROME_PX + CLOUD_EDGE_INSET_PX;
    return {
      left: CLOUD_EDGE_INSET_PX,
      top,
      width: Math.max(0, viewport.width - CLOUD_EDGE_INSET_PX * 2),
      height: Math.max(0, panelTopLimit - top),
    };
  }, [viewport.width, panelTopLimit]);

  return (
    <div ref={anchorRef}>
      <WheelScroller data-offer-overlay-scroll="">
        <RadioCardGroup<WheelItem>
          items={items}
          value={effectiveSelectedId}
          onChange={handleWheelChange}
          onConfirm={handleWheelConfirm}
          // ページ遷移直後（Stage フォーカスなし）でも ← / → のカード送りと
          // Enter の決定が効くようにする。進行中タブ表示中のみ有効。
          globalKeyboardNav={active}
          cardSize={cardSize}
          scaleFan={1}
          breakOut
          breakOutInsetPx={0}
          verticalCenterY={layout.centerY}
          cardScrollHandoff
          // 待機ショットチップ（body ポータル）上の wheel / スワイプも
          // このホイールの回転として受け付ける
          gestureSurfaceKey={WHEEL_GESTURE_KEY}
          // スクロール速度に応じてホイール全体を引き、やることの全体量を
          // 掴みやすくする（停止で等倍に戻る）。minScale 0 = ホイール裏側の
          // 逆さカードが覗かない自動クランプの下限まで最大ズームアウト。
          velocityZoom={{ minScale: 0 }}
          onSettleChange={handleSettleChange}
          // Shift ホールドで全カードを手札状の扇に集約し、やることの全体量を
          // 見渡す。件数は「待機中」センチネルを除いた実カード数を出す。
          // 進行中タブ表示中のみ有効。
          stackPreview={active ? { count: todoItems.length } : undefined}
          onStackChange={handleStackChange}
          ariaLabel="進行中のやることを選択"
          renderCard={(item, state) => {
            if (item.kind === "waiting") {
              return (
                <WaitingLabelCard
                  selected={state.checked}
                  title={waitingTitle}
                />
              );
            }
            if (item.kind === "tip") {
              return (
                <TipPortraitCard
                  card={item.card}
                  selected={state.checked}
                  // スタック中は選択カードの拡大を解いて山を揃える。
                  settled={wheelSettled && !stacked}
                  enlargeScale={tipEnlargeScale}
                />
              );
            }
            return (
              <CardFadeWrapper
                $fading={fadingShotId === item.shot.id}
                className={fadingShotId === item.shot.id ? "fade-out-bck" : undefined}
              >
                <ShotPortraitCard
                  shot={item.shot}
                  selected={state.checked}
                  number={shotNumbers.get(item.id) ?? 1}
                  playMedia={state.checked}
                />
              </CardFadeWrapper>
            );
          }}
        />
      </WheelScroller>

      {/* 相手側の未解決メッセージ雲（ショット選択時のみ、ナッジ完了で表示） */}
      {active && selectedItem && selectedItem.kind === "shot" && (
        <ShotCommentCloud
          key={selectedItem.id}
          shot={selectedItem.shot}
          projectUserPosition={projectUserPosition}
          cardRect={cloudCardRect}
          bounds={cloudBounds}
          visible={wheelSettled && !stacked}
          onRevealComplete={handleCloudRevealComplete}
        />
      )}

      {/* 待機中ショットの雲（待機中スロット選択時のみ、ナッジ完了から少し
          間を置いてラベルの周囲に散らす） */}
      {active && selectedItem && selectedItem.kind === "waiting" && (
        <WaitingShotCloud
          shots={waitingItems}
          cardRect={cloudCardRect}
          bounds={cloudBounds}
          visible={wheelSettled && !stacked}
          onShotClick={(shot, el) => navigateToPreview(shot, false, el)}
          wheelGestureKey={WHEEL_GESTURE_KEY}
        />
      )}

      {/* 下部パネル。待機中スロット選択時は表示しない */}
      {active &&
        selectedItem &&
        selectedItem.kind !== "waiting" &&
        createPortal(
          <ActionPanel
            ref={actionRef}
            $bottom={actionPanelBottom}
            $left={panelLeft}
            $width={panelWidth}
            $visible={panelVisible && !stacked}
          >
            {/* 提出 / 資料を送る。移動中の一時的な非表示でも登録は維持し、
                下部バー上の行へ切り替わってちらつかないようにする。
                ショットのパネルは選択中ショットへの「提出」等を持つため描画しない */}
            <ProjectPrimaryActionSlot hidden={selectedItem.kind === "shot"} />
            {selectedItem.kind === "shot" && (
              <ShotActions
                key={selectedItem.id}
                shot={selectedItem.shot}
                projectUserPosition={projectUserPosition}
                onNavigateToPreview={(shot, openShotEditModal) =>
                  navigateToPreview(shot, openShotEditModal)
                }
                onShotStatusChanged={onShotStatusChanged}
                onFadingChange={handleFadingChange}
              />
            )}
            {selectedItem.kind === "tip" && (
              <TipActions
                key={selectedItem.id}
                card={selectedItem.card}
                projectId={projectId || ""}
              />
            )}
          </ActionPanel>,
          document.body,
        )}
    </div>
  );
};

export default InProgressWheel;
